import { ChakraV3, FloatingPopover, FloatingPopoverBody } from '@piccolohealth/ui';
import type { Editor, Range } from '@tiptap/core';
import React from 'react';
import { FaAngleDown, FaAngleUp } from 'react-icons/fa';

interface Props {
  editor: Editor;
}

export const SearchAndReplaceMenu = (props: Props) => {
  const { editor } = props;
  const disclosure = ChakraV3.useDisclosure();
  const findInputRef = React.useRef<HTMLInputElement>(null);

  const [searchTerm, setSearchTerm] = React.useState<string>('');
  const [replaceTerm, setReplaceTerm] = React.useState<string>('');
  const [matchCase, setMatchCase] = React.useState<boolean>(false);

  const isSearching = React.useMemo(() => {
    return editor.storage.searchAndReplace.searchTerm === searchTerm;
  }, [editor.storage.searchAndReplace.searchTerm, searchTerm]);

  const goToSelection = React.useCallback(() => {
    if (!editor) {
      return;
    }

    const { results, resultIndex } = editor.storage.searchAndReplace;
    const position: Range = results[resultIndex];

    if (!position) {
      return;
    }

    editor.commands.setTextSelection(position);

    const { node } = editor.view.domAtPos(editor.state.selection.anchor);

    if (node instanceof HTMLElement) {
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [editor]);

  const onClear = React.useCallback(() => {
    setSearchTerm('');
    editor.commands.setSearchTerm('');
    setReplaceTerm('');
    editor.commands.setReplaceTerm('');
    editor.commands.resetIndex();
  }, [editor.commands]);

  const onSearchChange = React.useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const onReplaceChange = React.useCallback((value: string) => {
    setReplaceTerm(value);
  }, []);

  const onMatchCaseChange = React.useCallback(
    (value: boolean) => {
      setMatchCase(value);
      editor.commands.setCaseSensitive(value);
    },
    [editor.commands],
  );

  const onFind = React.useCallback(() => {
    if (isSearching) {
      editor.commands.nextSearchResult();
      goToSelection();
      return;
    }

    editor.commands.setSearchTerm(searchTerm);
    goToSelection();
  }, [editor.commands, goToSelection, searchTerm, isSearching]);

  const onReplace = React.useCallback(() => {
    editor.commands.setReplaceTerm(replaceTerm);

    editor
      .chain()
      .command(({ state, dispatch }) => {
        const { results, resultIndex } = editor.storage.searchAndReplace;
        const result = results[resultIndex];

        if (!dispatch) {
          return false;
        }

        if (!result) {
          return false;
        }

        const { from, to } = result;

        dispatch(state.tr.insertText(replaceTerm, from, to));

        return true;
      })
      .run();

    goToSelection();
  }, [editor, goToSelection, replaceTerm]);

  const onReplaceAll = React.useCallback(() => {
    editor.commands.setReplaceTerm(replaceTerm);
    editor.commands.replaceAll();
  }, [editor.commands, replaceTerm]);

  const onNext = React.useCallback(() => {
    editor.commands.nextSearchResult();
    goToSelection();
  }, [editor.commands, goToSelection]);

  const onPrevious = React.useCallback(() => {
    editor.commands.previousSearchResult();
    goToSelection();
  }, [editor.commands, goToSelection]);

  const onFindKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        onPrevious();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        onFind();
      }
    },
    [onFind, onPrevious],
  );

  const onOpen = React.useCallback(() => {
    disclosure.onOpen();
    findInputRef.current?.focus();
  }, [disclosure]);

  const onClose = React.useCallback(() => {
    onClear();
    disclosure.onClose();
  }, [disclosure, onClear]);

  React.useEffect(() => {
    const onHotkey = (e: KeyboardEvent) => {
      if (
        e.keyCode === 114 ||
        (e.ctrlKey && e.keyCode === 70) ||
        e.keyCode === 114 ||
        (e.ctrlKey && e.keyCode === 70) ||
        (e.metaKey && e.keyCode === 70)
      ) {
        e.preventDefault();
        onOpen();
      }
    };

    window.addEventListener('keydown', onHotkey);

    return () => {
      window.removeEventListener('keydown', onHotkey);
    };
  });

  return (
    <FloatingPopover
      open={disclosure.open}
      setOpen={(open) => {
        if (!open) {
          onClose();
        }
      }}
      placement='right'
      closeOnScroll={false}
      shouldInitialFocus
      render={() => (
        <FloatingPopoverBody title='Search and replace' w='sm'>
          <ChakraV3.Stack gap={2}>
            <ChakraV3.FormField label='Find'>
              <ChakraV3.HStack>
                <ChakraV3.Input
                  ref={findInputRef}
                  value={searchTerm}
                  onChange={(e) => onSearchChange(e.target.value)}
                  onKeyDown={onFindKeyDown}
                  placeholder='Find in text...'
                  size='sm'
                />
                <ChakraV3.Tooltip content='Previous'>
                  <ChakraV3.IconButton
                    size='sm'
                    aria-label='Previous'
                    onClick={onPrevious}
                    tabIndex={-1}
                    variant='subtle'
                  >
                    <FaAngleUp />
                  </ChakraV3.IconButton>
                </ChakraV3.Tooltip>
                <ChakraV3.Tooltip content='Next'>
                  <ChakraV3.IconButton
                    size='sm'
                    aria-label='Next'
                    onClick={onNext}
                    tabIndex={-1}
                    variant='subtle'
                  >
                    <FaAngleDown />
                  </ChakraV3.IconButton>
                </ChakraV3.Tooltip>
              </ChakraV3.HStack>
            </ChakraV3.FormField>
            <ChakraV3.FormField label='Replace with'>
              <ChakraV3.Input
                value={replaceTerm}
                onChange={(e) => onReplaceChange(e.target.value)}
                placeholder='Replace with term...'
                size='sm'
              />
            </ChakraV3.FormField>

            <ChakraV3.FormField label='Settings'>
              <ChakraV3.HStack>
                <ChakraV3.Text fontSize='sm'>Match case</ChakraV3.Text>
                <ChakraV3.Spacer />
                <ChakraV3.Switch.Root
                  checked={matchCase}
                  onCheckedChange={(e) => onMatchCaseChange(e.checked)}
                  size='sm'
                  colorPalette='brand'
                >
                  <ChakraV3.Switch.HiddenInput />
                  <ChakraV3.Switch.Control />
                </ChakraV3.Switch.Root>
              </ChakraV3.HStack>
            </ChakraV3.FormField>

            <ChakraV3.HStack mt={2}>
              <ChakraV3.Text fontSize='sm' color='secondary'>
                {editor.storage.searchAndReplace.results.length > 0 &&
                  `${editor.storage.searchAndReplace.resultIndex + 1} / ${
                    editor.storage.searchAndReplace.results.length
                  }`}
              </ChakraV3.Text>
              <ChakraV3.Spacer />
              <ChakraV3.Button size='sm' variant='outline' onClick={onReplace} tabIndex={-1}>
                Replace
              </ChakraV3.Button>
              <ChakraV3.Button size='sm' variant='outline' onClick={onReplaceAll} tabIndex={-1}>
                Replace all
              </ChakraV3.Button>
              <ChakraV3.Button size='sm' colorPalette='brand' onClick={onFind}>
                Find
              </ChakraV3.Button>
            </ChakraV3.HStack>
          </ChakraV3.Stack>
        </FloatingPopoverBody>
      )}
    >
      <ChakraV3.Box pos='absolute' top={4} right={4} />
    </FloatingPopover>
  );
};
